<template>
  <b-row>
    <b-col cols="12">
      <b-row class="mb-1">
        <b-col cols="12">
          <b-card no-body>
            <template #header>
              <div class="d-flex flex-row">
                <h4 class="flex-grow-1">Specifications</h4>
                <b-button
                  variant="link"
                  v-if="!editingInfos"
                  @click="editingInfos = true"
                >
                  <i class="fa fa-pen" />
                </b-button>
              </div>
            </template>
            <b-card-body v-if="editableInfos">
              <EditableInput
                v-for="info of editableInfos"
                :key="info.key"
                :label="info.label"
                :editing="editingInfos"
                :value="info.value"
                @valueChanged="value => onValueChanged(value, info.key)"
              />
            </b-card-body>
            <b-card-footer v-if="editingInfos">
              <b-row>
                <b-col cols="12" class="text-right px-2">
                  <b-button @click="resetInfos" class="mr-2">
                    Cancel
                  </b-button>
                  <b-button @click="saveInfos" variant="primary">
                    Save
                  </b-button>
                </b-col>
              </b-row>
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="mb-1" v-if="canGetDeviceMapping">
        <b-col cols="12">
          <b-card no-body>
            <template #header>
              <div class="d-flex flex-row">
                <h4 class="flex-grow-1">Metadata</h4>
                <b-button
                  variant="link"
                  v-if="!editingMetadata"
                  @click="editingMetadata = true"
                >
                  <i class="fa fa-pen" />
                </b-button>
              </div>
            </template>
            <b-card-body class="asset-metadata-form">
              <Form
                v-if="editingMetadata"
                :formSchema="metadataFormSchema"
                :model="metadata"
              />
              <b-row
                v-else
                class="text-left"
                v-for="field of metadataFields"
                :key="field.key"
              >
                <b-col cols="2">
                  <span> {{ field.label }} </span>
                </b-col>
                <b-col cols="10">
                  {{ get(field.key) }}
                </b-col>
              </b-row>
            </b-card-body>
            <b-card-footer v-if="editingMetadata">
              <b-row>
                <b-col cols="12" class="text-right px-2">
                  <b-button @click="resetMetadata" class="mr-2">
                    Cancel
                  </b-button>
                  <b-button @click="saveMetadata" variant="primary">
                    Save
                  </b-button>
                </b-col>
              </b-row>
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="mb-1" v-else>
        <b-col cols="12">
          <b-card>
            <h1 class="float-left display-3 mr-4">
              <i class="fa fa-lock"></i>
            </h1>
            <h4 class="pt-3">You are not allowed to see device metadata.</h4>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="mb-1" v-if="canSearchAsset">
        <b-col cols="12">
          <b-card class="text-left">
            <template #header>
              <div class="d-flex flex-row">
                <h4 class="flex-grow-1">Linked Asset</h4>
                <b-button variant="link" @click="link('link-asset-modal')">
                  <i class="fa fa-link" />
                </b-button>
              </div>
            </template>
            <b-row class="text-left font-weight-bold mb-1">
              <b-col cols="12">
                ID
              </b-col>
            </b-row>
            <b-row
              align-v="center"
              class="text-left"
              v-for="asset of linkedAssets"
              :key="asset.id"
            >
              <b-col cols="9">
                <router-link
                  :to="{ name: 'asset', params: { assetId: asset.id } }"
                  >{{ asset.id }}</router-link
                >
              </b-col>
              <b-col cols="3" v-if="canUnlinkAsset">
                <b-button
                  variant="link"
                  @click="unlink(device.id)"
                  class="pull-right"
                  ><i class="fa fa-unlink"
                /></b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="mb-1" v-else>
        <b-col cols="12">
          <b-card>
            <h1 class="float-left display-3 mr-4">
              <i class="fa fa-lock"></i>
            </h1>
            <h4 class="pt-3">
              You are not allowed to see device linked assets.
            </h4>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <b-card class="text-left">
            <template #header>
              <div class="d-flex flex-row">
                <h4 class="flex-grow-1">Last Measures</h4>
                <b-button
                  variant="link"
                  title="Show last measurements in JSON format"
                  v-b-modal.viewLastMeasureAsJSON
                >
                  <i class="fas fa-file-alt"></i>
                </b-button>
              </div>
            </template>
            <template v-if="Object.keys(measures).length">
              <b-row
                class="text-left"
                v-for="[measureType, measurePayload] of measures"
                :key="measureType"
              >
                <b-col cols="12">
                  <span
                    ><b> Last {{ measureType }}: </b></span
                  >
                </b-col>
                <b-col cols="12">
                  {{
                    defaultTo(
                      getMeasureByType(measurePayload, measureType),
                      'N/A'
                    )
                  }}
                </b-col>
                <b-col cols="12" v-if="measurePayload.updatedAt">
                  {{ new Date(measurePayload.updatedAt) }}
                </b-col>
              </b-row>
            </template>
            <template v-else>This asset has no measures yet.</template>
          </b-card>
        </b-col>
      </b-row>
    </b-col>
    <link-asset-modal :deviceId="deviceId" @confirmLinkAsset="onConfirmLink" />
    <b-modal id="viewLastMeasureAsJSON" title="Last measure">
      <pre v-if="Object.keys(measures).length">{{
        JSON.stringify(measures, null, 2)
      }}</pre>
    </b-modal>
  </b-row>
</template>

<script>
import { watch, onMounted } from '@vue/composition-api';
import EditableInput from '@/components/device-manager/EditableInput';
import {
  FormSchemaService,
  MappingFieldsService
} from '@/components/vue-shared-components';
import LinkAssetModal from './LinkAssetModal.vue';
import { useDetails } from '@/composition/details';
import { getMeasureByType } from '@/helpers/measures';
import defaultTo from 'lodash/defaultTo';

export default {
  components: {
    EditableInput,
    LinkAssetModal
  },
  props: {
    payload: {
      type: Object,
      required: true
    },
    metadataMapping: {
      type: Object,
      required: true
    },
    deviceId: {
      type: String,
      required: true
    },
    linkedAssets: {
      type: Array,
      default: () => []
    },
    canUpdateDevice: {
      type: Boolean,
      required: true
    },
    canGetDeviceMapping: {
      type: Boolean,
      required: true
    },
    canUnlinkAsset: {
      type: Boolean,
      required: true
    },
    canMLinkAsset: {
      type: Boolean,
      required: true
    },
    canSearchAsset: {
      type: Boolean,
      required: true
    }
  },
  setup(props, ctx) {
    const details = useDetails(props, ctx);

    details.editableInfos.value.shift();

    watch(
      () => props.payload,
      value => {
        details.editableInfos.value.shift();
        details.metadata.value = _.get(value, '_source.metadata', {});
      }
    );

    onMounted(() => {
      const formSchemaService = new FormSchemaService();
      const mappingFieldsService = new MappingFieldsService();

      details.metadata.value = _.get(props.payload, '_source.metadata', {});

      details.metadataFormSchema.value = formSchemaService.generate(
        props.metadataMapping.properties,
        details.metadata.value
      );
      details.metadataFormSchema.value.fields = details.metadataFormSchema.value.fields.map(
        f => {
          f.label = details.formatMetaLabel(f.label);
          return f;
        }
      );
      details.metadataFields.value = mappingFieldsService.getFieldsForTable(
        props.metadataMapping
      );
      details.metadataFields.value = details.metadataFields.value.map(m => {
        m.label = details.formatMetaLabel(m.label);
        return m;
      });
      details.resetEditedInfos();
    });

    return {
      ...details,
      getMeasureByType,
      defaultTo
    };
  }
};
</script>

<style lang="scss">
.asset-metadata-form .vue-form-generator {
  .field-input {
    text-align: left;
    width: 100%;
  }

  label {
    width: 137px;
  }

  .field-wrap {
    display: inline-block;
    width: calc(100% - 142px);
  }
}
</style>
